import { create } from 'zustand'

import { api } from '@/api/client'
import type { AddTrackResponse, Daylist, LibraryItem, Provider } from '@/api/types'
import { toQueueItem, usePlayer, type PlaySource, type QueueItem } from '@/audio/store'
import { toast } from '@/components/ui'
import { isIncognito, useIncognito } from '@/lib/incognito'

interface Preparation {
  daylistId: number | string | null
  name: string
  ready: number
  total: number
  cancel: () => void
}

let run = 0
export const useDaylistPreparation = create<Preparation>((set) => ({
  daylistId: null, name: '', ready: 0, total: 0,
  cancel: () => { run += 1; set({ daylistId: null }) },
}))

/** Wait for the ordered mix, not just the songs that happened to be cached.
 * Lives outside the page so navigation cannot discard the playback intent.
 * A new playback command or incognito change cancels the pending handoff.
 */
export async function playDaylist(daylist: Daylist, shuffle = false) {
  if (daylist.status !== 'ready') return
  return playCollection({ id: daylist.id, name: daylist.name, tracks: daylist.entries.map((entry) => entry.track) }, shuffle, 'daylist')
}

export async function playCollection(collection: { id: number | string; name: string; startFrom?: 'chosen' | 'anywhere'; tracks: Array<{ provider: Provider; external_id: string }> }, shuffle = false, source: PlaySource = 'album') {
  if (!collection.tracks.length) return
  useDaylistPreparation.getState().cancel()
  const token = run
  usePlayer.getState().pause()
  const intent = usePlayer.getState().intentVersion
  const incognito = isIncognito()
  const cancelled = () => token !== run
  const stopPlayer = usePlayer.subscribe((state) => {
    if (state.intentVersion !== intent && !cancelled()) useDaylistPreparation.getState().cancel()
  })
  const stopPrivacy = useIncognito.subscribe((state) => {
    if (state.active !== incognito && !cancelled()) useDaylistPreparation.getState().cancel()
  })
  useDaylistPreparation.setState({ daylistId: collection.id, name: collection.name, ready: 0, total: collection.tracks.length })
  const items: Array<LibraryItem | null> = Array(collection.tracks.length).fill(null)
  const updateProgress = () => {
    if (!cancelled()) useDaylistPreparation.setState({ ready: items.filter((item) => item?.stream_url).length })
  }
  let cursor = 0
  try {
    await Promise.all(Array.from({ length: Math.min(4, collection.tracks.length) }, async () => {
      while (cursor < collection.tracks.length && !cancelled()) {
        const index = cursor++
        const track = collection.tracks[index]
        try {
          const response = await api.post<AddTrackResponse>('/playback/tracks', {
            provider: track.provider, external_id: track.external_id, incognito,
          })
          items[index] = response.library_item
        } catch {
          // Unavailable songs are reported once after the rest of the mix resolves.
        }
        updateProgress()
      }
    }))
    const deadline = Date.now() + 10 * 60 * 1000
    while (!cancelled()) {
      const pending = items.map((item, index) => ({ item, index })).filter(({ item }) =>
        item && !item.stream_url && ['pending', 'downloading'].includes(item.status),
      )
      if (!pending.length) break
      if (Date.now() >= deadline) throw new Error('Downloads are taking longer than expected. Try playing again shortly.')
      await new Promise((resolve) => setTimeout(resolve, 3000))
      if (cancelled()) return
      await Promise.all(pending.map(async ({ item, index }) => {
        items[index] = await api.get<LibraryItem>(`/library/tracks/${item!.id}`)
      }))
      updateProgress()
    }
    if (cancelled()) return
    const queue = items.map((item) => item ? toQueueItem(item) : null).filter((item): item is QueueItem => item !== null)
    if (!queue.length) throw new Error('These songs could not be prepared. Check Downloads and try again.')
    stopPlayer()
    stopPrivacy()
    const player = usePlayer.getState()
    if (shuffle && !player.shuffle) player.toggleShuffle()
    player.playQueue(queue, 0, { source, startFrom: collection.startFrom ?? 'anywhere' })
    const skipped = collection.tracks.length - queue.length
    if (skipped) toast.info(`Playing ${queue.length} of ${collection.tracks.length} songs`, `${skipped} could not be downloaded. See Downloads for details.`)
  } catch (error) {
    if (!cancelled()) toast.error('Could not start playback', error instanceof Error ? error.message : 'Please try again.')
  } finally {
    stopPlayer()
    stopPrivacy()
    if (!cancelled()) useDaylistPreparation.setState({ daylistId: null })
  }
}
